/**
 * Thank You Page - Paid Subscription Success
 * URL: /thank-you-subscription
 * Used for analytics tracking of paid subscriptions
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Crown, Zap, BookOpen, Users, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";
const POLL_MS = 3000;
const MAX_POLLS = 6;

export default function ThankYouSubscription({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [state, setState] = useState("checking"); // checking | active | pending | failed

  const sessionId = searchParams.get("session_id");
  const userTier = user?.subscription_tier;

  // Confirm the payment with Stripe (the backend activates the plan on the spot) and poll briefly if it isn't settled yet
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let attempts = 0;

    const refreshUser = async () => {
      if (!setUser) return;
      const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      if (res.ok) setUser(await res.json());
    };

    const check = async () => {
      attempts += 1;
      try {
        const response = await fetch(`${API}/api/subscription/verify-session?session_id=${encodeURIComponent(sessionId)}`, { credentials: "include" });
        const data = response.ok ? await response.json() : { success: false };
        if (cancelled) return;
        setSubscriptionTier(data.subscription_tier || "student");
        if (data.active) {
          setState("active");
          await refreshUser();
        } else if (data.payment_status === "paid" && !data.in_progress && !data.duplicate && attempts >= 2) {
          setState("failed"); // Stripe took the money but the account didn't flip - support will sort it
        } else if (attempts < MAX_POLLS) {
          setTimeout(check, POLL_MS);
          return;
        } else {
          setState(data.payment_status === "paid" ? "failed" : "pending");
        }
      } catch (error) {
        console.error("Error verifying subscription:", error);
        if (!cancelled) setState(attempts < MAX_POLLS ? "checking" : "pending");
        if (!cancelled && attempts < MAX_POLLS) { setTimeout(check, POLL_MS); return; }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [sessionId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // No session id in the URL (e.g. revisiting the page): show the account's current plan
  useEffect(() => {
    if (sessionId) return;
    if (userTier) { setSubscriptionTier(userTier); setState(userTier !== "free" ? "active" : "pending"); }
    setLoading(false);
  }, [sessionId, userTier]);

  const isSchoolPlan = subscriptionTier === "school" || subscriptionTier === "school_student";
  const planName = isSchoolPlan ? "School Plan" : subscriptionTier === "teacher_plus" ? "Teacher Plus" : subscriptionTier === "teacher_pro" ? "Teacher Pro" : "Student Plan";
  const checking = state === "checking";

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-3" data-testid="thank-you-checking">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Confirming your payment with Stripe…</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Crown className="w-10 h-10 text-yellow-500" />
          </motion.div>

          {/* Heading */}
          <h1 className="text-4xl font-bold mb-4" data-testid="thank-you-heading">
            {state === "active" ? "Thank You for Subscribing! 🎉" : state === "failed" ? "Payment received - one more step" : "Thanks! Finishing up…"}
          </h1>
          
          {state === "active" ? (
            <p className="text-xl text-muted-foreground mb-2" data-testid="thank-you-status">
              Your <span className="text-primary font-semibold">{planName}</span> is now active!
            </p>
          ) : state === "failed" ? (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4 text-left" data-testid="thank-you-status">
              <p className="text-amber-700 dark:text-amber-400 font-medium">Stripe confirmed your payment, but your account hasn't switched to the {planName} yet.</p>
              <p className="text-sm text-muted-foreground mt-1">Please don't pay again - you won't be charged twice. Refresh this page in a minute, and if it still shows Free, email us from your Profile page and we'll switch it on by hand.</p>
            </div>
          ) : (
            <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-4 mb-4 text-left" data-testid="thank-you-status">
              <p className="text-sky-700 dark:text-sky-400 font-medium flex items-center gap-2">{checking && <Loader2 className="w-4 h-4 animate-spin" />}Stripe is still confirming your payment.</p>
              <p className="text-sm text-muted-foreground mt-1">Your {planName} switches on automatically the moment it clears - usually within a minute. Please don't start another checkout.</p>
            </div>
          )}
          
          <p className="text-muted-foreground mb-8">
            {state === "active" ? "You now have full access to all premium features." : "Here's what you'll get:"}
          </p>

          {/* Premium Features Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-6 text-left"
            >
              <Zap className="w-8 h-8 text-yellow-500 mb-3" />
              <h3 className="font-semibold mb-2">Unlimited Questions</h3>
              <p className="text-sm text-muted-foreground">
                No daily limits - practice as much as you want
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-card border border-border rounded-xl p-6 text-left"
            >
              <BookOpen className="w-8 h-8 text-yellow-500 mb-3" />
              <h3 className="font-semibold mb-2">All Drills Access</h3>
              <p className="text-sm text-muted-foreground">
                Access every drill in our collection
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-card border border-border rounded-xl p-6 text-left"
            >
              <CheckCircle className="w-8 h-8 text-yellow-500 mb-3" />
              <h3 className="font-semibold mb-2">Challenge Questions</h3>
              <p className="text-sm text-muted-foreground">
                Unlock advanced challenges with hints
              </p>
            </motion.div>

            {isSchoolPlan && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-card border border-border rounded-xl p-6 text-left"
              >
                <Users className="w-8 h-8 text-yellow-500 mb-3" />
                <h3 className="font-semibold mb-2">School Features</h3>
                <p className="text-sm text-muted-foreground">
                  Manage students, assign drills, track progress
                </p>
              </motion.div>
            )}
          </div>

          {/* CTA Button */}
          <Button
            size="lg"
            onClick={() => navigate("/")}
            className="gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
            data-testid="start-premium-btn"
          >
            Start Your Premium Experience
            <ArrowRight className="w-4 h-4" />
          </Button>

          {/* Receipt info */}
          <p className="text-sm text-muted-foreground mt-6">
            A receipt has been sent to your email address.
            <br />
            Manage your subscription in{" "}
            <button
              onClick={() => navigate("/profile")}
              className="text-primary hover:underline"
            >
              Profile Settings
            </button>
          </p>
        </motion.div>
      </div>
  );
}
